import { Form, Row, Col } from 'react-bootstrap';
import { useMemo, useState } from 'react';
import { errorAlert, infoAlert, successAlert, warningAlert } from '../../components/toastr';
import { SubmitButton, CancelButton } from '../../components/btns';
// import File from '../../components/files';
import SelectInput from '../../components/select';
import { useEffect } from 'react';
import { getApprovedRequisitions } from '../../resources/api/requisitions';
import { getApprovedPaymentsGrouped } from '../../resources/api/payroll';
import { UGX } from '../../components/currency';
import { createVoucher, updateVoucher } from '../../resources/api/payment-vouchers';
import { assortedPaymentMethods, paymentMethods } from '../../assets/constants';
import { UCWords } from '../../components/resources';
import { getAccounts } from '../../resources/api/accounts';


const { Control: { Feedback }, Label } = Form;


/**
 * 
 * @param {Object} props
 * @param {"create"|"edit"} props.action 
 * @param {import('../../resources/api/payment-vouchers').PaymentVoucherObject} props.details 
 * @param {(details: import('../../resources/api/payment-vouchers').PaymentVoucherObject) => void} props.onSubmit 
 */
const VoucherForm = ({ action = "create", details: voucherObject, onSubmit }) => {

    const [details, setDetails] = useState(voucherObject);
    const [requisition_ids, setRequisitions] = useState(voucherObject.requisitions?.map(r => r.id) || []);
    const [staff_payment_ids, setStaffPayments] = useState(voucherObject.staff_payments_grouped?.map(p => p.id) || []);

    // const [deleted_items, setDeletedItems] = useState([]);

    // const [files, setFiles] = useState([]);
    const [items, setItems] = useState({ requisitions: [], staff_payments: [] });
    const [accounts, setAccounts] = useState([]);

    const [validated, setValidated] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);


    useEffect(() => {

        infoAlert("Loading the various lists list...");


        getApprovedRequisitions(true)
            .then(({ requisitions }) => {

                requisitions = requisitions.filter(r => (!r.voucher_id || (!!details.id && (r.voucher_id === details.id))));

                setItems(d => ({
                    ...d,
                    requisitions: requisitions.map(c => ({ ...c, title: `${c.display_name} | ${UGX(c.total_amount).format()} | ${c.reference}` }))
                }));

                successAlert("Successfully loaded the requisitions list");
            })
            .catch(e => errorAlert(e));

        getApprovedPaymentsGrouped(true)
            .then(({ payments }) => {


                payments = payments.filter(r => (!r.voucher_id || (r.voucher_id === details.id)));

                setItems(d => ({
                    ...d,
                    staff_payments: payments.map(c => ({ ...c, title: `${c.display_name} | ${UGX(c.total_amount).format()}` }))
                }));

                successAlert("Successfully loaded the staff payments list");
            })
            .catch(e => errorAlert(e))

        getAccounts()
            .then(({ accounts }) => {
                if (accounts.length === 0)
                    warningAlert("No accounts available. Please add an account first.");
                else
                    successAlert("Loaded all accounts.");

                setAccounts(accounts);
            })
            .catch(errorAlert)
    }, [details.id]);


    const displayAccounts = useMemo(() => {

        const _types = assortedPaymentMethods[details.method] || [];

        return accounts.filter(a => _types.indexOf(a.account_type) !== -1);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [details.method, JSON.stringify(accounts)])



    /**
     * handle the overall submitting of the form
     * @param {React.FormEvent} e
     */
    const handleSubmit = e => {
        const form = e.currentTarget;

        e.preventDefault();

        if (!form.checkValidity()) {
            setValidated(true);
            errorAlert("You have errors in your form. These have been highlighted for you.", "Form Errors");
            return;
        }


        setValidated(false);
        setSubmitting(true);

        let payment_ids = [];

        staff_payment_ids.forEach(pid => payment_ids.push(items.staff_payments.find(p => p.id === pid).payment_ids));

        const data = {
            ...details, requisition_ids, staff_payment_ids: payment_ids.flat()
        }

        // console.log(data);

        // return;

        let promise;

        if (action === "create") {
            promise = createVoucher(data);
        } else {
            promise = updateVoucher(data, data.id)
        }

        promise
            .then(({ voucher, message }) => {
                successAlert(message);
                onSubmit(voucher);
            })
            .catch(e => {
                setSubmitting(false);
                errorAlert(e);
            })

    }


    return (

        <Form className="max-800" validated={validated} noValidate onSubmit={handleSubmit}>
            <h4 className="form-section-label mt-5">Details</h4>

            <Row>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Payment Method</Label>
                    <Form.Select
                        as="select"
                        value={details.method}
                        onChange={e => setDetails({ ...details, method: e.currentTarget.value, account_id: "" })}
                        required
                    >
                        <option value="">-</option>
                        {paymentMethods.map(c => <option key={c} value={c}>{UCWords(c).replace(/-/g, " ")}</option>)}
                    </Form.Select>
                    <Feedback type="invalid">
                        You must select a method for payment for this expense.
                    </Feedback>
                </Col>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Payment Account</Label>
                    <Form.Select
                        as="select"
                        value={details.account_id}
                        onChange={e => setDetails({ ...details, account_id: e.currentTarget.value })}
                        required
                    >
                        <option value="">-</option>
                        {displayAccounts.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </Form.Select>
                    <Feedback type="invalid">
                        You must select the account to which payment is being made.
                    </Feedback>
                    {!details.method &&
                        <Form.Text muted>
                            First select a payment method.
                        </Form.Text>
                    }
                </Col>
            </Row>


            <h4 className="form-section-label mt-5">Items</h4>
            <Row>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Requisitions</Label>
                    <SelectInput
                        value={requisition_ids || []}
                        onChange={ids => setRequisitions(ids || [])}
                        options={items.requisitions}
                        maxItems={null}
                    />
                </Col>
                <Col sm={6} className="my-1">
                    <Label className="form-field-title">Staff Payments</Label>
                    <SelectInput
                        value={staff_payment_ids || []}
                        onChange={ids => setStaffPayments(ids || [])}
                        options={items.staff_payments}
                        maxItems={null}
                    />
                </Col>
            </Row>


            <Row>
                <Col className="mt-4 mb-3 text-end">
                    <SubmitButton isSubmitting={isSubmitting} type="submit">
                        Submit Voucher
                    </SubmitButton>
                    <CancelButton isSubmitting={isSubmitting}>
                        Cancel
                    </CancelButton>
                </Col>
            </Row>

        </Form >
    )
}


export default VoucherForm;